import {Chip, createStyles, Stack, Tooltip} from '@mantine/core';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SelectableCard from 'ui-library/inputs/SelectableCard';

import RoutineFieldHeader from './RoutineFieldHeader';
import {TriggerType} from './RoutineTypes';

interface TriggerTypeSelectorProps {
  value: TriggerType | null;
  onChange: (value: TriggerType) => void;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
  disabled?: boolean;
}

const useStyles = createStyles(() => ({
  chipGroup: {
    display: 'flex',
    gap: 12,
  },
  chipWrapper: {
    flex: 1,
    // Hide the built-in check/uncheck circle icons from SelectableCard;
    // they are the first two SVG children inside the Mantine Group.
    '& .mantine-Group-root > svg': {
      display: 'none !important',
    },
    // Vertically center the custom icon with the title text
    '& .mantine-Group-root > div:first-of-type': {
      alignSelf: 'center',
    },
  },
  disabledWrapper: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
}));

export default function TriggerTypeSelector({
  value,
  onChange,
  timeBasedEnabled,
  eventBasedEnabled,
  disabled = false,
}: TriggerTypeSelectorProps): JSX.Element | null {
  const {classes, cx} = useStyles();

  const bothEnabled = timeBasedEnabled && eventBasedEnabled;
  if (!bothEnabled && !disabled) {
    return null;
  }

  const runLimit = BenchlingConfig.LLM_ROUTINE_EXECUTION_WINDOW_LIMIT;
  const windowMinutes = BenchlingConfig.LLM_ROUTINE_EXECUTION_WINDOW_MINUTES;
  const rateLimitTooltip = `Total number of agent runs cannot exceed ${runLimit} ${pluralize(
    runLimit,
    'run'
  )} in a ${windowMinutes} minute window for the tenant.`;

  const content = (
    <Stack spacing={4}>
      <RoutineFieldHeader title="When should this agent run?" titleTooltip={rateLimitTooltip} />
      <Chip.Group
        multiple={false}
        value={value ?? ''}
        onChange={(val: string) => {
          if (!disabled && val) {
            onChange(val as TriggerType);
          }
        }}
      >
        <div className={cx(classes.chipGroup, disabled && classes.disabledWrapper)}>
          <div className={classes.chipWrapper}>
            <SelectableCard
              value={TriggerType.TIME_BASED}
              title="Time based"
              icon={<BenchlingIcon icon="clock" />}
              disabled={disabled}
            />
          </div>
          <div className={classes.chipWrapper}>
            <SelectableCard
              value={TriggerType.EVENT_BASED}
              title="Event based"
              icon={<BenchlingIcon icon="sparkle" />}
              disabled={disabled}
            />
          </div>
        </div>
      </Chip.Group>
    </Stack>
  );

  if (disabled) {
    return (
      <Tooltip label="Cannot edit trigger type" position="top" withArrow={true}>
        {content}
      </Tooltip>
    );
  }

  return content;
}
